import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon, CogIcon, ChartBarIcon, PencilIcon, SaveIcon } from './Icons';

const SetupScreen = ({ onBack, onClassSelected, sheetsService }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [showClassSelection, setShowClassSelection] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [selectedClassForStats, setSelectedClassForStats] = useState('');
  const [classStats, setClassStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [editingSettings, setEditingSettings] = useState(false);
  const [editForm, setEditForm] = useState({ passcode: '', courses: '' });
  const [selectedClass, setSelectedClass] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Load settings when component mounts
  useEffect(() => {
    if (sheetsService) {
      loadSettings();
    }
  }, [sheetsService]);

  const loadSettings = async () => {
    try {
      setLoadingSettings(true);
      const appSettings = await sheetsService.getSettings();
      setSettings(appSettings);
      setEditForm({
        passcode: appSettings.passcode,
        courses: appSettings.courses.join(', ')
      });
    } catch (error) {
      console.error('Error loading settings:', error);
      alert('Failed to load settings. Using defaults.');
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (!settings) {
      setError('Settings not loaded. Please try again.');
      return;
    }
    
    if (code === settings.passcode) {
      setShowClassSelection(true);
      setError('');
    } else {
      setError('Invalid setup code. Please try again.');
      setTimeout(() => {
        onBack(); // Return to homescreen after 2 seconds
      }, 2000);
    }
  };

  const handleClassSelect = (selectedClass) => {
    // Save class to localStorage
    localStorage.setItem('selectedClass', selectedClass);
    // Set the selected class and show success state
    setSelectedClass(selectedClass);
    setShowSuccess(true);
    // Don't call onClassSelected here - let teacher decide when to go back
  };

  const handleViewStats = async (className) => {
    if (!className) {
      alert('Please select a course to view statistics');
      return;
    }

    setSelectedClassForStats(className);
    setLoadingStats(true);
    
    try {
      const stats = await sheetsService.getClassSignoutStats(className);
      setClassStats(stats);
      setShowStats(true);
    } catch (error) {
      console.error('Error fetching class stats:', error);
      alert('Error fetching class statistics. Please try again.');
    } finally {
      setLoadingStats(false);
    }
  };

  const handleStatsCourseChange = async (event) => {
    const selectedCourse = event.target.value;
    if (selectedCourse) {
      setSelectedClassForStats(selectedCourse);
      setLoadingStats(true);
      
      try {
        const stats = await sheetsService.getClassSignoutStats(selectedCourse);
        setClassStats(stats);
      } catch (error) {
        console.error('Error fetching class stats:', error);
        alert('Error fetching class statistics. Please try again.');
      } finally {
        setLoadingStats(false);
      }
    }
  };

  const handleOpenStats = () => {
    setShowStats(true);
    setSelectedClassForStats(''); // Reset to show dropdown
    setClassStats([]); // Clear previous stats
  };

  const handleBackToClassSelection = () => {
    setShowStats(false);
    setClassStats([]);
    setSelectedClassForStats('');
  };

  const handleSaveSettings = async () => {
    try {
      const coursesArray = editForm.courses.split(',').map(course => course.trim()).filter(course => course);
      
      if (!editForm.passcode.trim()) {
        alert('Passcode cannot be empty');
        return;
      }
      
      if (coursesArray.length === 0) {
        alert('At least one course must be specified');
        return;
      }

      await sheetsService.updateSettings({
        passcode: editForm.passcode.trim(),
        courses: coursesArray
      });

      // Reload settings
      await loadSettings();
      setEditingSettings(false);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      passcode: settings.passcode,
      courses: settings.courses.join(', ')
    });
    setEditingSettings(false);
  };

  const handleBackToHome = () => {
    // Now call onClassSelected when teacher explicitly chooses to continue
    if (selectedClass) {
      onClassSelected(selectedClass);
    } else {
      onBack();
    }
  };

  if (showStats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="max-w-4xl w-full bg-white rounded-lg shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Class Statistics</h2>
                <p className="text-gray-600 mt-1">View signout statistics for any class</p>
              </div>
            </div>
            <button
              onClick={handleBackToClassSelection}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
            </button>
          </div>
          
          <div className="mb-6">
            <label htmlFor="courseSelect" className="block text-sm font-medium text-gray-700 mb-2">
              Select Course
            </label>
            <select
              id="courseSelect"
              value={selectedClassForStats || ''}
              onChange={handleStatsCourseChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Choose a course...</option>
              {settings?.courses?.map((course) => (
                <option key={course} value={course}>
                  {course}
                </option>
              ))}
            </select>
          </div>
          
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-blue-600 text-sm font-semibold">ℹ️</span>
              </div>
              <div className="text-sm text-blue-800">
                <strong>Past 28 Days:</strong> Showing signout frequency for all students in the selected class
              </div>
            </div>
          </div>

          {!selectedClassForStats ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gray-400 text-2xl">📊</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a course</h3>
              <p className="text-gray-500">Choose a course from the dropdown above to view statistics.</p>
            </div>
          ) : loadingStats ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-gray-500">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading statistics...
              </div>
            </div>
          ) : classStats.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gray-400 text-2xl">📊</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No signout data found</h3>
              <p className="text-gray-500">No students have signed out from {selectedClassForStats} in the past 28 days.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {classStats.map((student, index) => (
                <div
                  key={student.name}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 ${
                        index === 0 ? 'bg-yellow-100 text-yellow-800' :
                        index === 1 ? 'bg-gray-100 text-gray-800' :
                        index === 2 ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        <span className="text-sm font-semibold">
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{student.name}</div>
                        <div className="text-sm text-gray-500">
                          {student.signoutCount} signout{student.signoutCount !== 1 ? 's' : ''} in past 28 days
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${
                        student.signoutCount >= 10 ? 'text-red-600' :
                        student.signoutCount >= 7 ? 'text-orange-600' :
                        student.signoutCount >= 4 ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {student.signoutCount}
                      </div>
                      <div className="text-xs text-gray-500">
                        {student.signoutCount >= 10 ? 'Frequent' :
                         student.signoutCount >= 7 ? 'Moderate' :
                         student.signoutCount >= 4 ? 'Occasional' :
                         'Rare'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (showSettings) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
                <p className="text-gray-600 mt-1">Manage passcode and course names</p>
              </div>
            </div>
            <button
              onClick={() => setShowSettings(false)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {loadingSettings ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-gray-500">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading settings...
              </div>
            </div>
          ) : settings ? (
            <div className="space-y-6">
              {editingSettings ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Setup Passcode
                    </label>
                    <input
                      type="text"
                      value={editForm.passcode}
                      onChange={(e) => setEditForm({ ...editForm, passcode: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter setup passcode"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Course Names (comma-separated)
                    </label>
                    <textarea
                      value={editForm.courses}
                      onChange={(e) => setEditForm({ ...editForm, courses: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter course names separated by commas"
                    />
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={handleSaveSettings}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                    >
                      <SaveIcon className="w-4 h-4 mr-2 inline" />
                      Save Changes
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">Setup Passcode</h3>
                        <p className="text-gray-600 mt-1">Current: {settings.passcode}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">Course Names</h3>
                        <div className="mt-2 space-y-1">
                          {settings.courses.map((course, index) => (
                            <div key={index} className="text-sm text-gray-600">• {course}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => setEditingSettings(true)}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                  >
                    <PencilIcon className="w-4 h-4 mr-2 inline" />
                    Edit Settings
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-400 text-2xl">⚠️</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Settings not available</h3>
              <p className="text-gray-500">Could not load settings from Sheet2.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (showClassSelection) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="max-w-4xl w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-6">
            <CogIcon className="mx-auto h-12 w-12 text-blue-600 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900">Select Your Class</h2>
            <p className="text-gray-600 mt-2">Click on a class to select it as your current class</p>
          </div>
          
          {showSuccess && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-green-600 text-sm font-semibold">✓</span>
                </div>
                <div className="text-sm text-green-800">
                  <strong>Success!</strong> {selectedClass} has been selected as your current class.
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-3 mb-6">
            {settings?.courses?.map((className) => (
              <div
                key={className}
                onClick={() => handleClassSelect(className)}
                className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                  showSuccess && selectedClass === className
                    ? 'border-green-500 bg-green-50 hover:bg-green-100'
                    : 'border-gray-200 hover:bg-blue-50 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium text-gray-900">{className}</div>
                  <div className={`text-sm ${
                    showSuccess && selectedClass === className
                      ? 'text-green-600 font-semibold'
                      : 'text-blue-600'
                  }`}>
                    {showSuccess && selectedClass === className ? '✓ Selected' : 'Click to select'}
                  </div>
                </div>
              </div>
            )) || (
              <div className="text-center py-8 text-gray-500">
                No courses available. Please check your Google Sheet configuration.
              </div>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleOpenStats}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              📊 View Statistics
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
            >
              <CogIcon className="w-4 h-4 mr-2 inline" />
              Manage Settings
            </button>
            <button
              onClick={showSuccess ? handleBackToHome : () => setShowClassSelection(false)}
              className={`flex-1 py-2 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 ${
                showSuccess
                  ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                  : 'bg-gray-500 text-white hover:bg-gray-600 focus:ring-gray-500'
              }`}
            >
              {showSuccess ? (
                <>
                  <span className="mr-2">✓</span>
                  Continue to Home
                </>
              ) : (
                <>
                  <ArrowLeftIcon className="w-4 h-4 mr-2 inline" />
                  Back to Code Entry
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-6">
          <CogIcon className="mx-auto h-12 w-12 text-blue-600 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900">Setup Access</h2>
          <p className="text-gray-600 mt-2">Enter the setup code to configure your class</p>
        </div>
        
        <form onSubmit={handleCodeSubmit} className="space-y-4">
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
              Setup Code
            </label>
            <input
              type="password"
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter setup code"
              required
            />
          </div>
          
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            Continue
          </button>
        </form>
        
        <div className="mt-4">
          <button
            onClick={onBack}
            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetupScreen;
